import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export const api = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 60000,
  withCredentials: true,
});

// Auth
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const exchangeSession = (session_id) =>
  api.post("/auth/session", { session_id }).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);

// Profile
export const getProfile = () => api.get("/profile").then((r) => r.data);
export const updateProfile = (data) => api.put("/profile", data).then((r) => r.data);

// Cycle
export const getPredictions = () => api.get("/cycle/predictions").then((r) => r.data);
export const getCalendar = (year, month) =>
  api.get(`/cycle/calendar?year=${year}&month=${month}`).then((r) => r.data);
export const logCycle = (data) => api.post("/cycle/log", data).then((r) => r.data);
export const listCycleLogs = () => api.get("/cycle/log").then((r) => r.data);
export const deleteCycleLog = (id) => api.delete(`/cycle/log/${id}`).then((r) => r.data);

// Care directory
export const listDoctors = (params = {}) => api.get("/care/doctors", { params }).then((r) => r.data);
export const getDoctor = (id) => api.get(`/care/doctors/${id}`).then((r) => r.data);
export const careFilters = () => api.get("/care/filters").then((r) => r.data);

// Community
export const listCommunityPosts = (topic) => api.get("/community/posts", { params: topic ? { topic } : {} }).then((r) => r.data);
export const getCommunityPost = (id) => api.get(`/community/posts/${id}`).then((r) => r.data);
export const createCommunityPost = (payload) => api.post("/community/posts", payload).then((r) => r.data);
export const reactToPost = (id, kind) => api.post(`/community/posts/${id}/react`, { kind }).then((r) => r.data);
export const reportPost = (id) => api.post(`/community/posts/${id}/report`).then((r) => r.data);
export const listComments = (id) => api.get(`/community/posts/${id}/comments`).then((r) => r.data);
export const addComment = (id, body) => api.post(`/community/posts/${id}/comments`, { body }).then((r) => r.data);
export const listCommunityTopics = () => api.get("/community/topics").then((r) => r.data);

// Journal prompts + streak
export const getJournalPrompts = (phase = "any") => api.get("/journal/prompts", { params: { phase } }).then((r) => r.data);
export const getJournalStreak = () => api.get("/journal/streak").then((r) => r.data);
export const saveReflection = (payload) => api.post("/reflection", payload).then((r) => r.data);

// Transactional email (Resend)
export const emailReflectionSummary = (payload) => api.post("/email/reflection-summary", payload).then((r) => r.data);
export const getEmailStatus = () => api.get("/email/status").then((r) => r.data);

// Push notifications
export const getPushPublicKey = () => api.get("/genesis/notifications/push/public-key").then((r) => r.data);
export const pushSubscribe = (subscription, userAgent) => api.post("/genesis/notifications/push/subscribe", { subscription, user_agent: userAgent }).then((r) => r.data);
export const pushUnsubscribe = (endpoint) => api.post("/genesis/notifications/push/unsubscribe", { endpoint }).then((r) => r.data);
export const getPushStatus = () => api.get("/genesis/notifications/push/status").then((r) => r.data);

// Hydration
export const logHydration = (glasses = 1) => api.post("/wellness/hydration", { glasses }).then((r) => r.data);
export const getHydrationToday = () => api.get("/wellness/hydration/today").then((r) => r.data);
export const resetHydrationToday = () => api.delete("/wellness/hydration/today").then((r) => r.data);

// Breathing
export const logBreathing = (data) => api.post("/wellness/breathing", data).then((r) => r.data);
export const listBreathing = () => api.get("/wellness/breathing").then((r) => r.data);

// Wellness rings + weekly report + articles
export const getWellnessRings = () => api.get("/wellness/rings/today").then((r) => r.data);
export const getWeeklyReport = () => api.get("/insight/weekly").then((r) => r.data);
export const listArticles = (category) => api.get(`/articles${category ? `?category=${encodeURIComponent(category)}` : ""}`).then((r) => r.data);
export const getArticle = (id) => api.get(`/articles/${id}`).then((r) => r.data);
export const listArticleCategories = () => api.get("/articles/categories/list").then((r) => r.data);

// Wellness
export const logMood = (data) => api.post("/wellness/mood", data).then((r) => r.data);
export const listMood = () => api.get("/wellness/mood").then((r) => r.data);
export const logSleep = (data) => api.post("/wellness/sleep", data).then((r) => r.data);
export const listSleep = () => api.get("/wellness/sleep").then((r) => r.data);
export const logFitness = (data) => api.post("/wellness/fitness", data).then((r) => r.data);
export const listFitness = () => api.get("/wellness/fitness").then((r) => r.data);
export const logSkin = (data) => api.post("/wellness/skin", data).then((r) => r.data);
export const listSkin = () => api.get("/wellness/skin").then((r) => r.data);
export const logNutrition = (data) => api.post("/wellness/nutrition", data).then((r) => r.data);
export const listNutrition = () => api.get("/wellness/nutrition").then((r) => r.data);

// Coach
export const sendChat = (message, history) =>
  api.post("/coach/chat", { message, history }).then((r) => r.data);
export const getInsights = (refresh = false) =>
  api.get(`/coach/insights${refresh ? "?refresh=true" : ""}`).then((r) => r.data);
export const getRecommendations = (refresh = false) =>
  api.get(`/coach/recommendations${refresh ? "?refresh=true" : ""}`).then((r) => r.data);
export const getCoachHistory = () => api.get("/coach/history").then((r) => r.data);

// Safety
export const listContacts = () => api.get("/safety/contacts").then((r) => r.data);
export const addContact = (data) => api.post("/safety/contacts", data).then((r) => r.data);
export const deleteContact = (id) => api.delete(`/safety/contacts/${id}`).then((r) => r.data);
export const triggerSOS = () => api.post("/safety/sos").then((r) => r.data);
export const sosHistory = () => api.get("/safety/sos/history").then((r) => r.data);

// Dashboard + Analytics
export const getDashboard = () => api.get("/dashboard").then((r) => r.data);
export const getTrends = (days = 30) =>
  api.get(`/analytics/trends?days=${days}`).then((r) => r.data);


// Pregnancy
export const getPregnancyProfile = () => api.get("/pregnancy/profile").then((r) => r.data);
export const upsertPregnancyProfile = (data) => api.post("/pregnancy/profile", data).then((r) => r.data);
export const deletePregnancyProfile = () => api.delete("/pregnancy/profile").then((r) => r.data);
export const getPregnancyState = () => api.get("/pregnancy/state").then((r) => r.data);
export const getPregnancyWeek = (week) => api.get(`/pregnancy/week/${week}`).then((r) => r.data);
export const logPregnancySymptom = (data) => api.post("/pregnancy/symptom", data).then((r) => r.data);
export const listPregnancySymptoms = () => api.get("/pregnancy/symptom").then((r) => r.data);
export const getPregnancyInsight = (refresh = false) =>
  api.get(`/pregnancy/insight${refresh ? "?refresh=true" : ""}`).then((r) => r.data);

// Streak
export const getAppStreak = () => api.get("/streak").then((r) => r.data);

// Cycle forecast
export const getCycleForecast = () => api.get("/cycle/forecast").then((r) => r.data);

// Symptom patterns
export const getSymptomPatterns = () => api.get("/wellness/patterns").then((r) => r.data);

// Weight
export const logWeight = (data) => api.post("/wellness/weight", data).then((r) => r.data);
export const listWeight = () => api.get("/wellness/weight").then((r) => r.data);

// Weekly digest email
export const sendWeeklyDigest = () => api.post("/email/weekly-digest").then((r) => r.data);

// Push notifications (Knya-native VAPID)
export const getVapidKey = () => api.get("/push/vapid-key").then((r) => r.data);
export const knyaPushSubscribe = (sub) => api.post("/push/subscribe", sub).then((r) => r.data);
export const knyaPushUnsubscribe = (sub) => api.delete("/push/unsubscribe", { data: sub }).then((r) => r.data);
export const pushTest = () => api.post("/push/test").then((r) => r.data);

// Health report PDF — returns blob
export const downloadHealthReport = () =>
  api.get("/export/health-report", { responseType: "blob" }).then((r) => r.data);
